import * as Sentry from "@sentry/browser";
import {
  type BrowserOptions,
  browserTracingIntegration,
  replayIntegration,
} from "@sentry/browser";

export const isDev = process.env.NODE_ENV === "development";
// NOTE(ALEXA): Default sampling rates - can be overridden per-app via SamplingOptions
// Conservative default to protect against new high-traffic apps burning quota.
// Low-traffic internal apps can explicitly set 100% for full visibility.
const DEFAULT_PAGELOAD_SAMPLE_RATE = 0.2; // 20% - safe default for unknown traffic
const DEFAULT_TRACES_SAMPLE_RATE = 0.1; // 10% - safe default for unknown traffic

export type SamplingOptions = {
  pageloadSampleRate?: number; // 0-1, default 0.2 (20%)
  tracesSampleRate?: number; // 0-1, default 0.1 (10%)
};

export const mergeOptions = (
  config: BrowserOptions,
  disableReplay = false,
  sampling: SamplingOptions = {}
): BrowserOptions => {
  const {
    pageloadSampleRate = DEFAULT_PAGELOAD_SAMPLE_RATE,
    tracesSampleRate = DEFAULT_TRACES_SAMPLE_RATE,
  } = sampling;

  const integrations: BrowserOptions["integrations"] = [
    browserTracingIntegration(),
  ];

  if (!disableReplay) {
    integrations.push(
      replayIntegration({
        blockAllMedia: true,
      })
    );
  }

  return {
    environment: process.env.SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,
    release: process.env.APP_VERSION,
    sendDefaultPii: true,
    integrations,
    tracePropagationTargets: [/^https:\/\/[^/]*.liftoff.io/],
    tracesSampler: (samplingContext) => {
      // Check if this is a pageload transaction.
      // In Sentry v10, sentry.op is undefined at sampling time, but sentry.origin
      // contains the source (e.g., "auto.pageload.browser" for pageloads).
      const origin = samplingContext.attributes?.["sentry.origin"] as
        | string
        | undefined;
      const isPageload = origin?.includes("pageload") ?? false;

      if (isPageload) {
        return pageloadSampleRate;
      }
      return tracesSampleRate;
    },
    // NOTE: ALI we are ignoring these errors to reduce noise
    // https://forum.sentry.io/t/typeerror-failed-to-fetch-reported-over-and-overe/8447
    ignoreErrors: [
      "TypeError: Failed to fetch",
      "TypeError: NetworkError when attempting to fetch resource.",
      "ResizeObserver loop limit exceeded",
    ],
    ...config,
  };
};

if (window) {
  // Capture unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    Sentry.captureException(event.reason);
  });

  // Capture global errors
  window.onerror = (message, _source, _lineno, _colno, error) =>
    Sentry.captureException(error || new Error(message as string));
}

export type InitSentryOptions = Partial<{
  disabled: boolean;
  disableReplay: boolean;
  getUser: UserGetter | null;
  sampling: SamplingOptions;
}>;

export type User = {
  email: string;
};

export type UserGetter = string | Promise<User>;

const fetchUser = async (url: string): Promise<User | undefined> => {
  const resp = await fetch(url);
  if (resp.status === 401) return undefined;
  const contentType = resp.headers.get("Content-Type");
  if (resp.ok && contentType?.startsWith("application/json")) {
    return (await resp.json()) as User;
  }
  throw new Error(`User info fetch response: ${resp.status} ${contentType}`);
};

const isUserPromise = (getUser: UserGetter): getUser is Promise<User> =>
  typeof getUser === "object" && "then" in getUser;

const resolveUser = async (getUser: UserGetter): Promise<User | undefined> => {
  try {
    return await (isUserPromise(getUser) ? getUser : fetchUser(getUser));
  } catch (err) {
    Sentry.captureException(err);
  }
};

export async function configureUserScope(
  sentry: typeof Sentry,
  getUser?: UserGetter | null
): Promise<void> {
  if (getUser === null) return;
  let getter = getUser;
  if (getter === undefined) getter = "/user_info";
  (async () => {
    const user = await resolveUser(getter);
    if (user?.email) {
      sentry.setUser({ email: user.email });
    }
  })();
}
